import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, User, Wrench, ChevronLeft, ChevronRight, CheckCircle2, Moon, FileUp, FileText, Edit3, XCircle, AlertTriangle, IndianRupee, Truck } from 'lucide-react';
import { api } from '@/api/client';

interface SummaryRow {
  userId: number; name: string; email: string; empCode?: string; userType: string;
  ordersCreated: number; ordersEdited: number; ordersCancelled: number; ordersUploaded: number;
  ndrActions: number; remittanceActions: number; trackingScans: number; freightActions: number;
  wmsActions: number; walletActions: number; invoiceActions: number; creditNoteActions: number;
  otherActions: number; totalActions: number; lastActiveAt: string | null; isIdle: boolean; idleDays: number;
}
interface SummaryResp { days: number; since: string; totalUsers: number; activeUsers: number; idleUsers: number; rows: SummaryRow[]; }

interface LogRow {
  id: string; actorType: string; actorId?: number | null; actorName?: string | null; actorEmpCode?: string | null;
  action: string; entity: string; entityId?: string | null; ip?: string | null; userAgent?: string | null; createdAt: string; after?: any;
}

const ACTION_COLORS: Record<string, string> = {
  post: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  patch: 'bg-amber-50 text-amber-700 border-amber-200',
  put: 'bg-amber-50 text-amber-700 border-amber-200',
  delete: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function ActivityLogPanel() {
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: summary } = useQuery({
    queryKey: ['audit-summary', days],
    queryFn: async () => (await api.get<SummaryResp>('/audit-log/summary', { params: { days } })).data,
  });

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (search) p.search = search;
    if (entity) p.entity = entity;
    if (action) p.action = action;
    return p;
  }, [search, entity, action, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', params],
    queryFn: async () => (await api.get('/audit-log', { params })).data as { total: number; retentionDays?: number; items: LogRow[] },
  });

  const total = data?.total ?? 0;
  const retentionDays = data?.retentionDays ?? 7;
  const items = data?.items ?? [];
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Activity size={18} /> Activity Log</h2>
          <p className="text-xs text-slate-500 mt-0.5">Per-user breakdown + raw audit trail across all modules</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1.5 text-sm">
          <option value={1}>Today</option>
          <option value={7}>Last 7 days</option>
          <option value={15}>Last 15 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <KpiCard icon={<CheckCircle2 className="text-emerald-600" />} label="Active users" value={summary.activeUsers} sub={`of ${summary.totalUsers} total`} />
          <KpiCard icon={<Moon className="text-amber-600" />} label={`Idle (${days}d)`} value={summary.idleUsers} sub="no activity in window" tone="amber" />
          <KpiCard icon={<Activity className="text-indigo-600" />} label="Total actions" value={summary.rows.reduce((s, r) => s + r.totalActions, 0)} sub={`since ${new Date(summary.since).toLocaleDateString('en-IN')}`} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 border-b">Per User · Last {days} Days</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50/60 text-slate-500 text-[11px]">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-center px-2 py-2" title="Orders Created"><FileText size={12} className="inline" /> Created</th>
                <th className="text-center px-2 py-2" title="Orders Uploaded (Bulk)"><FileUp size={12} className="inline" /> Uploaded</th>
                <th className="text-center px-2 py-2" title="Orders Edited"><Edit3 size={12} className="inline" /> Edited</th>
                <th className="text-center px-2 py-2" title="Orders Cancelled"><XCircle size={12} className="inline" /> Cancelled</th>
                <th className="text-center px-2 py-2" title="NDR Actions"><AlertTriangle size={12} className="inline" /> NDR</th>
                <th className="text-center px-2 py-2" title="Remittance Actions"><IndianRupee size={12} className="inline" /> Remit</th>
                <th className="text-center px-2 py-2" title="Tracking Scans"><Truck size={12} className="inline" /> Scans</th>
                <th className="text-center px-2 py-2">Freight</th>
                <th className="text-center px-2 py-2">Invoices</th>
                <th className="text-center px-2 py-2">Wallet</th>
                <th className="text-center px-2 py-2">WMS</th>
                <th className="text-center px-2 py-2 font-semibold">Total</th>
                <th className="text-left px-2 py-2">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {!summary && <tr><td colSpan={14} className="text-center py-6 text-slate-400">Loading…</td></tr>}
              {summary?.rows.map(r => (
                <tr key={r.userId} className={`border-t ${r.isIdle ? 'bg-amber-50/30' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white ${r.isIdle ? 'bg-slate-400' : 'bg-indigo-500'}`}>{r.name[0] ?? '?'}</div>
                      <div>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-[10px] text-slate-400">{r.empCode ?? r.userType.replace(/_/g,' ')}</div>
                      </div>
                    </div>
                  </td>
                  <Cell n={r.ordersCreated} />
                  <Cell n={r.ordersUploaded} />
                  <Cell n={r.ordersEdited} />
                  <Cell n={r.ordersCancelled} color="rose" />
                  <Cell n={r.ndrActions} />
                  <Cell n={r.remittanceActions} />
                  <Cell n={r.trackingScans} />
                  <Cell n={r.freightActions} />
                  <Cell n={r.invoiceActions} />
                  <Cell n={r.walletActions} />
                  <Cell n={r.wmsActions} />
                  <td className={`px-2 py-2 text-center font-semibold ${r.isIdle ? 'text-slate-400' : 'text-slate-900'}`}>{r.totalActions}</td>
                  <td className="px-2 py-2 text-[11px] text-slate-500">
                    {r.lastActiveAt ? (
                      <>{new Date(r.lastActiveAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}{r.idleDays > 0 && <div className="text-amber-700">{r.idleDays}d idle</div>}</>
                    ) : <span className="text-rose-500">No activity</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-600 mb-2">
          Raw event log · {total} events
          <span className="font-normal text-slate-400"> · last {retentionDays} days (older events are deleted nightly)</span>
        </div>
        <div className="grid grid-cols-4 gap-3 mb-3">
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search entity / id..." className="border border-slate-300 rounded px-2 py-1.5 text-sm" />
          <select value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }} className="border border-slate-300 rounded px-2 py-1.5 text-sm">
            <option value="">All entities</option>
            <option value="orders">Orders</option><option value="merchants">Merchants</option><option value="couriers">Couriers</option>
            <option value="users">Users</option><option value="ndr">NDR</option><option value="tracking">Tracking</option>
            <option value="freight">Freight</option><option value="invoices">Invoices</option><option value="wallet">Wallet</option>
            <option value="remittance">Remittance</option><option value="credit-notes">Credit Notes</option>
          </select>
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="border border-slate-300 rounded px-2 py-1.5 text-sm">
            <option value="">All actions</option>
            <option value="post">Create</option><option value="patch">Update</option><option value="delete">Delete</option>
          </select>
          <button onClick={() => { setSearch(''); setEntity(''); setAction(''); setPage(1); }} className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50">Reset</button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-left px-3 py-2">Details</th>
                <th className="text-left px-3 py-2">Entity ID</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>}
              {!isLoading && items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">No activity yet</td></tr>}
              {items.map(it => (
                <tr key={it.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(it.createdAt).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {it.actorType === 'user' ? <User size={12} className="text-indigo-600" /> : <Wrench size={12} className="text-slate-500" />}
                      <div>
                        <div className="text-xs font-medium">{it.actorName ?? it.actorType}</div>
                        <div className="text-[10px] text-slate-400">{it.actorEmpCode ?? `${it.actorType} ${it.actorId ?? ''}`}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 border rounded ${ACTION_COLORS[it.action] ?? 'bg-slate-50 text-slate-700 border-slate-200'}`}>{it.action.toUpperCase()}</span></td>
                  <td className="px-3 py-2 font-medium">{it.entity}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 max-w-[220px] truncate" title={deriveDetail(it.after)}>{deriveDetail(it.after) || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.entityId ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{it.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3 text-sm">
          <div className="text-slate-500">Page {page} of {lastPage} · {total} total</div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-30"><ChevronLeft size={14} /></button>
            <button disabled={page >= lastPage} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pull a human-readable label (e.g. the merchant/company/order name) out of the captured response
// snapshot so the log reads "who created WHAT", not just an entity id.
function deriveDetail(after: any): string {
  if (!after || typeof after !== 'object') return '';
  const a: any = after.data && typeof after.data === 'object' ? after.data : after;
  const pick = a.companyName ?? a.brandName ?? a.employeeName ?? a.name ?? a.orderNo ?? a.awbNo ?? a.lrNo ?? a.invoiceNo ?? a.docNo ?? a.title ?? a.code ?? a.email;
  return pick ? String(pick) : '';
}

function Cell({ n, color = 'slate' }: { n: number; color?: string }) {
  const cls = n === 0 ? 'text-slate-300' : color === 'rose' ? 'text-rose-700 font-medium' : 'text-slate-700 font-medium';
  return <td className={`px-2 py-2 text-center ${cls}`}>{n || '·'}</td>;
}

function KpiCard({ icon, label, value, sub, tone = 'slate' }: { icon: React.ReactNode; label: string; value: number; sub?: string; tone?: string }) {
  const bg = tone === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200';
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">{icon} {label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
